import { registerComponent } from "helpers";
import WYATTElement from "wyatt";

/**
 * Accessible menu-button for a list of links (APG Menu Button, Links).
 *
 * Expects light DOM:
 * - [data-trigger] — toggle control (button)
 * - [data-menu] — menu panel
 * - [data-item] or [role="menuitem"] — focusable menu items
 * - [data-search] — optional search input that filters options
 * - [data-empty] — optional empty-state message for filtered results
 */
registerComponent(
  "link-menu",
  class LinkMenu extends WYATTElement {
    constructor() {
      super();

      this.currentFocusIndex = 0;
      this.trigger = this.querySelector("[data-trigger]");
      this.menu = this.querySelector("[data-menu]");
      this.search = this.querySelector("[data-search]");
      this.empty = this.querySelector("[data-empty]");
      this.items = Array.from(
        this.menu.querySelectorAll('[data-item], [role="menuitem"], a, button'),
      );

      this.items.forEach((item) => {
        this.ensureItemAttributes(item);
        item.addEventListener("keydown", this.handleMenuItemKeydown.bind(this));
      });

      this.trigger.addEventListener(
        "keydown",
        this.handleTriggerKeydown.bind(this),
      );
      this.trigger.addEventListener(
        "click",
        this.handleTriggerClick.bind(this),
      );

      if (this.search) {
        this.search.addEventListener(
          "input",
          this.handleSearchInput.bind(this),
        );
        this.search.addEventListener(
          "keydown",
          this.handleSearchKeydown.bind(this),
        );
      }

      this.boundHandleOutsidePointer = this.handleOutsidePointer.bind(this);
      this.boundHandleEscape = this.handleEscape.bind(this);

      document.addEventListener("pointerdown", this.boundHandleOutsidePointer);
      document.addEventListener("keydown", this.boundHandleEscape);

      this.closeMenu(false);
    }

    ensureItemAttributes(item) {
      const row = item.closest(".link-menu__item");

      if (row && !row.hasAttribute("role")) {
        row.setAttribute("role", "none");
      }

      if (!item.hasAttribute("role")) {
        item.setAttribute("role", "menuitem");
      }

      if (!item.hasAttribute("tabindex")) {
        item.setAttribute("tabindex", "-1");
      }

      if (
        item.classList.contains("link-menu__option--active") &&
        !item.hasAttribute("aria-current")
      ) {
        item.setAttribute("aria-current", "true");
      }
    }

    disconnectedCallback() {
      document.removeEventListener(
        "pointerdown",
        this.boundHandleOutsidePointer,
      );
      document.removeEventListener("keydown", this.boundHandleEscape);
      this.cancelCloseTransition();
    }

    get visibleItems() {
      return this.items.filter((item) => {
        const row = item.closest(".link-menu__item") || item.parentElement;

        return row && !row.hidden;
      });
    }

    handleOutsidePointer(event) {
      if (!this.isOpen() || this.contains(event.target)) {
        return;
      }

      this.closeMenu(false);
    }

    handleEscape(event) {
      if (event.key !== "Escape" || !this.isOpen()) {
        return;
      }

      if (this.search?.value) {
        this.clearSearch();
        this.search.focus();
        event.stopPropagation();
        event.preventDefault();
        return;
      }

      this.closeMenu();
      event.stopPropagation();
      event.preventDefault();
    }

    handleTriggerClick(event) {
      event.stopPropagation();
      event.preventDefault();

      if (this.isOpen()) {
        this.closeMenu();
      } else {
        this.openMenu();
      }
    }

    handleTriggerKeydown(event) {
      let flag = false;

      switch (event.key) {
        case "ArrowDown":
          this.openMenu();
          this.focusFirstOption();
          flag = true;
          break;

        case "ArrowUp":
          this.openMenu();
          this.focusLastOption();
          flag = true;
          break;

        case "Enter":
        case " ":
          if (this.isOpen()) {
            this.closeMenu();
          } else {
            this.openMenu({ moveFocus: true });
          }
          flag = true;
          break;

        default:
          break;
      }

      if (flag) {
        event.stopPropagation();
        event.preventDefault();
      }
    }

    handleSearchInput() {
      this.filterItems(this.search.value);
      this.currentFocusIndex = 0;
    }

    handleSearchKeydown(event) {
      let flag = false;

      switch (event.key) {
        case "ArrowDown":
          this.focusFirstOption();
          flag = true;
          break;

        case "ArrowUp":
          this.focusLastOption();
          flag = true;
          break;

        case "Enter":
          flag = true;
          break;

        case "Tab":
          this.closeMenu(false);
          break;

        default:
          break;
      }

      if (flag) {
        event.stopPropagation();
        event.preventDefault();
      }
    }

    handleMenuItemKeydown(event) {
      let flag = false;
      const { visibleItems } = this;

      switch (event.key) {
        case "ArrowDown":
          this.setFocusToNext();
          flag = true;
          break;

        case "ArrowUp": {
          const atStart = this.currentFocusIndex === 0;

          if (atStart && this.search) {
            this.search.focus();
            this.currentFocusIndex = 0;
          } else {
            this.setFocusToPrevious();
          }
          flag = true;
          break;
        }

        case "Home":
          this.setFocusTo(visibleItems[0]);
          flag = true;
          break;

        case "End":
          this.setFocusTo(visibleItems[visibleItems.length - 1]);
          flag = true;
          break;

        case "Tab":
          this.closeMenu(false);
          break;

        default:
          break;
      }

      if (flag) {
        event.stopPropagation();
        event.preventDefault();
      }
    }

    filterItems(query) {
      const normalized = query.trim().toLowerCase();
      const visibleCount = this.items.reduce((count, item) => {
        const row = item.closest(".link-menu__item") || item.parentElement;

        if (!row) {
          return count;
        }

        const label = (
          item.dataset.filterText ||
          item.querySelector(".link-menu__option-label")?.textContent ||
          item.textContent
        )
          .trim()
          .toLowerCase();
        const match = !normalized || label.includes(normalized);

        row.hidden = !match;

        return match ? count + 1 : count;
      }, 0);

      if (this.empty) {
        this.empty.hidden = visibleCount > 0;
      }
    }

    clearSearch() {
      if (!this.search) {
        return;
      }

      this.search.value = "";
      this.filterItems("");
    }

    focusFirstOption() {
      const [first] = this.visibleItems;

      if (first) {
        this.setFocusTo(first);
      }
    }

    focusLastOption() {
      const { visibleItems } = this;
      const last = visibleItems[visibleItems.length - 1];

      if (last) {
        this.setFocusTo(last);
      }
    }

    setFocusToNext() {
      const { visibleItems } = this;
      const atEnd = this.currentFocusIndex >= visibleItems.length - 1;

      if (atEnd) {
        this.setFocusTo(visibleItems[0]);
      } else {
        this.setFocusTo(visibleItems[this.currentFocusIndex + 1]);
      }
    }

    setFocusToPrevious() {
      const { visibleItems } = this;
      const atStart = this.currentFocusIndex <= 0;

      if (atStart) {
        this.setFocusTo(visibleItems[visibleItems.length - 1]);
      } else {
        this.setFocusTo(visibleItems[this.currentFocusIndex - 1]);
      }
    }

    setFocusTo(element) {
      if (!element) {
        return;
      }

      const { visibleItems } = this;
      const newIndex = visibleItems.indexOf(element);

      element.focus();

      if (this.contains(document.activeElement)) {
        this.currentFocusIndex = Math.max(newIndex, 0);
      } else {
        this.closeMenu();
      }
    }

    openMenu({ moveFocus = false } = {}) {
      this.cancelCloseTransition();
      this.dataset.open = "true";
      this.menu.hidden = false;
      this.trigger.setAttribute("aria-expanded", "true");
      this.closeOtherMenus();

      if (!moveFocus) {
        return;
      }

      if (this.search) {
        this.search.focus();
        this.currentFocusIndex = 0;
      } else {
        this.focusFirstOption();
      }
    }

    closeMenu(focusOnTrigger = true) {
      const wasOpen = this.isOpen();

      this.dataset.open = "false";
      this.trigger.setAttribute("aria-expanded", "false");
      this.clearSearch();

      if (focusOnTrigger) {
        this.trigger.focus();
      }

      this.currentFocusIndex = 0;

      if (!wasOpen) {
        this.menu.hidden = true;
        return;
      }

      this.boundFinishClose = (event) => {
        if (event.target !== this.menu || event.propertyName !== "opacity") {
          return;
        }

        this.finishClose();
      };

      this.menu.addEventListener("transitionend", this.boundFinishClose);
    }

    finishClose() {
      this.menu.hidden = true;
      this.cancelCloseTransition();
    }

    cancelCloseTransition() {
      if (!this.boundFinishClose) {
        return;
      }

      this.menu.removeEventListener("transitionend", this.boundFinishClose);
      this.boundFinishClose = null;
    }

    isOpen() {
      return this.dataset.open === "true";
    }

    closeOtherMenus() {
      Array.from(document.querySelectorAll("link-menu")).forEach((menu) => {
        if (menu === this) {
          return;
        }

        menu.closeMenu?.(false);
      });
    }
  },
);
